/**
 * CDP SDK Core Events
 * Implementation of page, track, identify, group methods
 */

import type {
  CDPCoreConfig,
  EventPayload,
  PageEventPayload,
  TrackEventPayload,
  IdentifyEventPayload,
  GroupEventPayload,
  EventContext,
  SessionData,
  DeviceFingerprint,
  BeforeSendCallback,
  AfterSendCallback,
} from './types';
import { DEFAULT_RETRY_CONFIG } from './types';
import { generateUUID, getTimestamp, log } from './utils';
import { nextEventContext, markFullContextSent } from './context';
import { canSendEvent, filterEventForConsent } from './consent';
import { sendEventWithRetry } from './transport';
import { enqueue, persistQueue } from './queue';
import { isBatchingEnabled, bufferEvent } from './batch';
import { isSensitivePath, scrubEventPayload } from './url-scrub';

// =============================================================================
// EVENT BUILDER
// =============================================================================

/**
 * Build base event payload
 */
function buildBasePayload(
  type: EventPayload['type'],
  anonymousId: string | null,
  userId: string | null,
  context: EventContext
): Omit<EventPayload, 'type'> & { type: EventPayload['type'] } {
  return {
    type,
    anonymousId,
    userId,
    timestamp: getTimestamp(),
    messageId: generateUUID(),
    context,
  };
}

// =============================================================================
// BOT DETECTION (client-side)
// =============================================================================

/**
 * Detect obvious bots before sending events to the server.
 * Catches headless browsers, known crawlers, and automation tools.
 */
function isBot(): boolean {
  if (typeof navigator === 'undefined') return false;

  // 1. Headless/automation browser detection
  if ((navigator as unknown as Record<string, unknown>).webdriver) return true;

  // 2. Known bot user-agent patterns
  const ua = navigator.userAgent;
  if (/bot[\/\s;)]/i.test(ua) || /crawler|spider|headlesschrome|phantomjs|prerender|lighthouse|applebot|googlebot|bingbot|yandexbot|baiduspider|duckduckbot|facebookexternalhit|twitterbot|linkedinbot|slackbot|semrushbot|ahrefsbot|bytespider|petalbot/i.test(ua)) {
    return true;
  }

  return false;
}

// Cache the result — UA doesn't change during page lifecycle
let _isBotCached: boolean | null = null;
function isBotCached(): boolean {
  if (_isBotCached === null) _isBotCached = isBot();
  return _isBotCached;
}

// =============================================================================
// SEND EVENT HELPER
// =============================================================================

let excludedPathsSource: () => readonly string[] | undefined = () => undefined;

/** The site's own `privacy.excludePaths` — read per event, so a profile refresh applies without a reload. */
export function setExcludedPathsSource(fn: () => readonly string[] | undefined): void {
  excludedPathsSource = fn;
}

function isFromSensitivePage(payload: EventPayload): boolean {
  let path = '';
  let host = '';
  const pageUrl = (payload.context as { page?: { url?: unknown } } | undefined)?.page?.url;
  if (typeof pageUrl === 'string' && pageUrl) {
    try {
      const u = new URL(pageUrl);
      path = u.pathname;
      host = u.hostname;
    } catch {
      /* fall back to the live location */
    }
  }
  if (!path && typeof window !== 'undefined' && window.location) {
    path = window.location.pathname;
    host = window.location.hostname;
  }
  return isSensitivePath(path, host, excludedPathsSource());
}

/**
 * Process and send an event
 */
export async function processAndSendEvent(
  payload: EventPayload,
  config: CDPCoreConfig,
  beforeSendCallbacks: BeforeSendCallback[],
  afterSendCallbacks: AfterSendCallback[]
): Promise<boolean> {
  // A password page (reset, forgot, change) sends nothing at all — page view, article event or
  // identify (Р-ID8, NEWS-LOGIN-IDENTITY-2026-10-26 §5.3). Judged by the page the event was BUILT
  // on, which is the current one except for an event created just before a navigation.
  if (isFromSensitivePage(payload)) {
    log('Event blocked: password page');
    return false;
  }

  // Block events from detected bots
  if (isBotCached()) {
    log('Event blocked: bot detected');
    return false;
  }

  // Check consent
  if (!canSendEvent(payload)) {
    return false;
  }

  // Filter event based on consent, then strip every URL of its secrets — a token-shaped
  // segment, a secret parameter's value, an e-mail address (Р-ID8).
  let processedPayload = scrubEventPayload(filterEventForConsent(payload));

  // Run beforeSend callbacks
  for (const callback of beforeSendCallbacks) {
    try {
      const result = callback(processedPayload);
      if (result === null) {
        log('Event cancelled by beforeSend callback:', payload.type);
        return false;
      }
      processedPayload = result;
    } catch (e) {
      log('Error in beforeSend callback:', e);
    }
  }

  // The event survived consent and every beforeSend, so it IS going out — only
  // now has this page view spent its one full context (see markFullContextSent).
  markFullContextSent();

  // Batching path: buffer the finalized event and let the batch layer ship it
  // (and run afterSend callbacks on flush). One request per ~batch instead of
  // one per event.
  if (isBatchingEnabled(config)) {
    return bufferEvent(processedPayload, config, afterSendCallbacks);
  }

  // Send the event
  const success = await sendEventWithRetry(
    config.apiHost,
    config.writeKey,
    processedPayload,
    DEFAULT_RETRY_CONFIG
  );

  // Run afterSend callbacks
  for (const callback of afterSendCallbacks) {
    try {
      callback(processedPayload, success);
    } catch (e) {
      log('Error in afterSend callback:', e);
    }
  }

  // If failed, queue for retry
  if (!success) {
    enqueue(processedPayload);
  }

  return success;
}

// =============================================================================
// PAGE EVENT
// =============================================================================

/**
 * Create a page event payload
 */
export function createPageEvent(
  anonymousId: string | null,
  userId: string | null,
  session: SessionData,
  fingerprint: DeviceFingerprint | null,
  name?: string,
  category?: string,
  properties?: Record<string, unknown>,
  /** SiteProfile.events.leanContext — full context only on the page's first event. */
  lean?: boolean
): PageEventPayload {
  const context = nextEventContext(session, fingerprint?.id, fingerprint ?? undefined, { lean });

  return {
    ...buildBasePayload('page', anonymousId, userId, context),
    type: 'page',
    name: name || document.title,
    category,
    properties: {
      ...properties,
      path: window.location.pathname,
      url: window.location.href,
      title: document.title,
      referrer: document.referrer,
      search: window.location.search || undefined,
    },
  };
}

// =============================================================================
// TRACK EVENT
// =============================================================================

/**
 * Create a track event payload
 */
export function createTrackEvent(
  anonymousId: string | null,
  userId: string | null,
  session: SessionData,
  fingerprint: DeviceFingerprint | null,
  event: string,
  properties?: Record<string, unknown>,
  /** SiteProfile.events.leanContext — full context only on the page's first event. */
  lean?: boolean
): TrackEventPayload {
  const context = nextEventContext(session, fingerprint?.id, fingerprint ?? undefined, { lean });

  return {
    ...buildBasePayload('track', anonymousId, userId, context),
    type: 'track',
    event,
    properties,
  };
}

// =============================================================================
// IDENTIFY EVENT
// =============================================================================

/**
 * Create an identify event payload
 */
export function createIdentifyEvent(
  anonymousId: string | null,
  userId: string | null,
  session: SessionData,
  fingerprint: DeviceFingerprint | null,
  traits?: Record<string, unknown>
): IdentifyEventPayload {
  // identify is ALWAYS full: identity resolution and the trait merge read
  // locale/timezone/browser/os, and it is one event per visitor, not five per
  // article — there is nothing to save and something to lose.
  const context = nextEventContext(session, fingerprint?.id, fingerprint ?? undefined, { forceFull: true });

  return {
    ...buildBasePayload('identify', anonymousId, userId, context),
    type: 'identify',
    traits,
  };
}

// =============================================================================
// GROUP EVENT
// =============================================================================

/**
 * Create a group event payload
 */
export function createGroupEvent(
  anonymousId: string | null,
  userId: string | null,
  session: SessionData,
  fingerprint: DeviceFingerprint | null,
  groupId: string,
  traits?: Record<string, unknown>
): GroupEventPayload {
  const context = nextEventContext(session, fingerprint?.id, fingerprint ?? undefined, { forceFull: true });

  return {
    ...buildBasePayload('group', anonymousId, userId, context),
    type: 'group',
    groupId,
    traits,
  };
}

// =============================================================================
// QUEUE FLUSH
// =============================================================================

/**
 * Flush queued events
 */
export async function flushQueue(
  config: CDPCoreConfig,
  beforeSendCallbacks: BeforeSendCallback[],
  afterSendCallbacks: AfterSendCallback[]
): Promise<void> {
  const { getQueue, requeueForRetry, setFlushing, isFlushing_, removeFromQueue } = await import('./queue');

  if (isFlushing_()) {
    log('Queue flush already in progress');
    return;
  }

  setFlushing(true);
  log('Flushing event queue...');

  try {
    const queue = getQueue();

    for (const queuedEvent of queue) {
      const payload = queuedEvent.payload;

      // Claim the entry: remove it from the live queue BEFORE processing so
      // a failed send re-enters exactly once via requeueForRetry. The old
      // clearMemoryQueue() at the end wiped the whole queue — including
      // just-requeued failures and events enqueued during the flush —
      // permanently dropping them.
      if (payload.messageId) {
        removeFromQueue(payload.messageId);
      }

      // Check consent
      if (!canSendEvent(payload)) {
        continue;
      }

      // Filter based on consent
      let processedPayload = filterEventForConsent(payload);

      // Run beforeSend callbacks
      let cancelled = false;
      for (const callback of beforeSendCallbacks) {
        try {
          const result = callback(processedPayload);
          if (result === null) {
            cancelled = true;
            break;
          }
          processedPayload = result;
        } catch (e) {
          log('Error in beforeSend callback:', e);
        }
      }

      if (cancelled) {
        continue;
      }

      // Send the event
      const success = await sendEventWithRetry(
        config.apiHost,
        config.writeKey,
        processedPayload,
        DEFAULT_RETRY_CONFIG
      );

      // Run afterSend callbacks
      for (const callback of afterSendCallbacks) {
        try {
          callback(processedPayload, success);
        } catch (e) {
          log('Error in afterSend callback:', e);
        }
      }

      // If failed, requeue
      if (!success) {
        requeueForRetry(queuedEvent);
      }
    }

    // No clearMemoryQueue() here: processed entries were claimed
    // individually above; whatever remains (retry re-entries + events
    // enqueued while flushing) must survive this flush.

    // Persist any remaining events
    persistQueue();

    log('Queue flush complete');
  } finally {
    setFlushing(false);
  }
}
